import React, { useEffect, useRef, useState } from 'react'

import { type DragZoomBrowser, useTrackDragZoom } from './useTrackDragZoom'

type IgvBrowser = Awaited<ReturnType<typeof import('igv').default.createBrowser>>

export type IgvTrack = {
  name: string
  format: string
  type?: string
  url: string
  /** Stable track id (e.g. the API `-coverage` / `-baf` / `-depth` suffix). igv.js keeps it as `track.id`. */
  id?: string
  indexURL?: string
  /** Stacking order; lower renders nearer the top (e.g. gene model above reads). */
  order?: number
  /** Max bp span at which the track draws; -1 = always visible (GEN-3110). */
  visibilityWindow?: number
  // Per-track display options applied from the settings popover (GEN-3105).
  /** Alignment: pair reads end-to-end. */
  viewAsPairs?: boolean
  /** Alignment: render soft-clipped bases. */
  showSoftClips?: boolean
  /** Alignment: shade reads by, e.g., 'strand'. */
  colorBy?: string
  /** Variant: 'EXPANDED' | 'COLLAPSED' | 'SQUISHED'. */
  displayMode?: string
  /** Annotation: shade features by their BED score (UCSC useScore, 0-1000). */
  useScore?: boolean
  /** Base feature color (CSS), shaded by score when `useScore` is set. */
  color?: string
  /** Score lower bound for `useScore` alpha mapping (defaults to 0). */
  min?: number
  /** Score upper bound for `useScore` alpha mapping (defaults to 1000). */
  max?: number
  /** Wig: disable per-view autoscaling so `min`/`max` set a fixed y-axis. */
  autoscale?: boolean
  /**
   * Wig: rendering style. igv.js defaults to 'bar' (fills from zero to each
   * value), so a scatter-style track (e.g. BAF) must set 'points' explicitly or
   * the bars swamp the signal (GEN-5292).
   */
  graphType?: 'bar' | 'points' | 'line'
  /** Fixed track height in px (e.g. the wig depth track). */
  height?: number
  /** Wig: horizontal guide lines, e.g. a baseline at y=1.0 (GEN-4447). */
  guideLines?: Array<{ y: number; color?: string; dotted?: boolean }>
}

/**
 * Custom genome reference for igv.js (GEN-3107). Supplied by the API as presigned
 * URLs when a case has CRAM/BAM alignments built against a non-default reference
 * (e.g. the Broad GRCh38 `Homo_sapiens_assembly38.fasta`). When present it
 * replaces `genome`; CRAM cannot be decoded unless igv.js loads this exact
 * reference, otherwise it raises an "MD5 checksum reference mismatch".
 */
export type IgvReference = {
  id: string
  name?: string
  fastaURL: string
  indexURL: string
}

export type IgvMountOptions = {
  genome?: string
  reference?: IgvReference | null
  locus?: string
  tracks?: IgvTrack[]
  /**
   * Locus (chr:pos or chr:start-end) to highlight as a region-of-interest band,
   * e.g. the currently selected variant (GEN-3104). Cleared when null/undefined.
   */
  roiLocus?: string | null
  /**
   * Enable drag-to-zoom on the genome-wide bedGraph wig tracks (coverage / BAF /
   * CNV depth), so a sweep across the plot zooms the browser to that span
   * (GEN-5709). Alignment / variant tracks and the ruler keep native behavior.
   */
  dragZoomWigTracks?: boolean
}

export type IgvMountProps = IgvMountOptions & {
  /**
   * Stable identifier that, when changed, forces a remount of the IGV browser.
   * Use this when callers want to reload tracks without a full page navigation.
   */
  reloadKey?: string | number
  className?: string
  /**
   * Applied to the IGV host element. Defaults to a fixed min height; pass e.g.
   * `min-h-full` to let the viewer fill a flex/grid column (GEN-3301).
   */
  hostClassName?: string
  ['data-testid']?: string
  onError?: (message: string) => void
  onReady?: () => void
}

/**
 * Mounts an `igv.js` browser into a host div with the given options and
 * disposes it on unmount. Kept deliberately free of variant/route concerns so
 * it can back both the single-variant viewer and the multi-track test harness
 * (GEN-2880 / GEN-2611).
 */
const IgvMount: React.FC<IgvMountProps> = ({
  genome,
  reference,
  locus,
  tracks,
  roiLocus,
  dragZoomWigTracks,
  reloadKey,
  className,
  hostClassName,
  onError,
  onReady,
  ...rest
}) => {
  const hostRef = useRef<HTMLDivElement | null>(null)
  const browserRef = useRef<IgvBrowser | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    let disposed = false
    let createdBrowser: IgvBrowser | null = null

    setError(null)
    setReady(false)

    // A custom reference (presigned fasta + fai) takes precedence over a named
    // genome id: igv.js accepts either `reference` or `genome`, not both.
    const options = {
      ...(reference
        ? {
            reference: {
              id: reference.id,
              ...(reference.name ? { name: reference.name } : {}),
              fastaURL: reference.fastaURL,
              indexURL: reference.indexURL,
            },
          }
        : { genome: genome ?? 'hg38' }),
      locus: locus ?? undefined,
      tracks: tracks ?? [],
    }

    async function mount() {
      const host = hostRef.current
      if (!host) return
      try {
        const { default: igv } = await import('igv')
        if (disposed) return
        createdBrowser = await igv.createBrowser(host, options as Parameters<typeof igv.createBrowser>[1])
        if (disposed) {
          igv.removeBrowser(createdBrowser)
          return
        }
        browserRef.current = createdBrowser
        setReady(true)
        onReady?.()
      } catch (e) {
        if (disposed) return
        const message = e instanceof Error ? e.message : 'Failed to load IGV browser'
        setError(message)
        onError?.(message)
      }
    }

    void mount()

    return () => {
      disposed = true
      browserRef.current = null
      const browserToDispose = createdBrowser
      if (browserToDispose) {
        void import('igv')
          .then(({ default: igv }) => {
            igv.removeBrowser(browserToDispose)
          })
          .catch(() => {
            /* unmount cleanup - safe to ignore */
          })
      }
    }
    // `locus` intentionally excluded: locus changes navigate the live browser
    // (effect below) instead of forcing an expensive track-reloading remount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [genome, reference, tracks, reloadKey, onError, onReady])

  // Navigate the already-mounted browser when the locus changes (e.g. the
  // variant table's "Open in IGV"), avoiding a full remount + track refetch.
  useEffect(() => {
    if (!locus || !ready) return
    const browser = browserRef.current as (IgvBrowser & { search?: (l: string) => void }) | null
    browser?.search?.(locus)
  }, [locus, ready])

  // Highlight the selected variant as a single region-of-interest band, and
  // clear it when nothing is selected (GEN-3104). Defensive about the ROI API
  // so a future igv.js change degrades to "no highlight" rather than a crash.
  useEffect(() => {
    if (!ready) return
    const browser = browserRef.current as
      | (IgvBrowser & { clearROIs?: () => void; loadROI?: (c: unknown) => unknown })
      | null
    if (!browser?.loadROI || !browser.clearROIs) return
    browser.clearROIs()
    if (!roiLocus) return
    const match = /^(.+):(\d+)(?:-(\d+))?$/.exec(roiLocus)
    if (!match) return
    const chr = match[1]
    const start = Math.max(0, Number.parseInt(match[2], 10) - 1)
    const end = match[3] ? Number.parseInt(match[3], 10) : Number.parseInt(match[2], 10)
    void browser.loadROI({
      name: 'Selected variant',
      color: '#d33682',
      features: [{ chr, start, end }],
    })
  }, [roiLocus, ready])

  // Drag-to-zoom on the genome-wide wig tracks (coverage / BAF / depth),
  // opt-in via `dragZoomWigTracks` (GEN-5709). Runs against the live browser
  // once ready; the hook no-ops when disabled or before mount.
  useTrackDragZoom(
    dragZoomWigTracks ? (browserRef.current as unknown as DragZoomBrowser | null) : null,
    ready,
    hostRef.current
  )

  // igv.js fits track widths to the container at creation and only re-fits via
  // its own window `resize` listener - not when its container changes width on
  // its own (dragging our side dividers) and, in our layout, not reliably on
  // window resize either. The Browser exposes no public `resize()`, but
  // `visibilityChange()` runs the same internal relayout (it calls the private
  // resize, recomputing viewport width). Observe the host and call it on any
  // size change so tracks fill the available width instead of leaving a gap.
  useEffect(() => {
    if (!ready) return
    const host = hostRef.current
    const browser = browserRef.current as (IgvBrowser & { visibilityChange?: () => void }) | null
    if (!host || !browser?.visibilityChange) return
    let frame = 0
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => browser.visibilityChange?.())
    })
    observer.observe(host)
    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
    }
  }, [ready])

  const testId = rest['data-testid'] ?? 'igv-mount'

  return (
    <div className={className}>
      <div
        ref={hostRef}
        data-testid={testId}
        className={`border-border-subtle rounded border bg-white ${hostClassName ?? 'min-h-96'}`}
      />
      {error && (
        <p role="alert" className="mt-2 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          Error loading IGV: {error}
        </p>
      )}
      {!error && !ready && (
        <p className="mt-2 text-xs text-gray-500" data-testid={`${testId}-status`}>
          Loading IGV browser...
        </p>
      )}
    </div>
  )
}

export default IgvMount
